import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft, FaArrowRight, FaCheckCircle, FaInfoCircle, FaPaperPlane } from 'react-icons/fa';
import { BreadcrumbSchema } from '../../components/SchemaMarkup';
import { destinations } from '../../data/destinations';
import { track } from '../../utils/analytics';
import './PlanTrip.css';

const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://safarnamah-backend-lc7p.onrender.com'
  : 'http://localhost:5001';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  destination: '',
  startDate: '',
  endDate: '',
  duration: '',
  budget: '',
  travelers: '1',
  travelWith: 'Solo',
  interests: [],
  accommodation: 'Standard',
  transportation: 'Flexible',
  mealPreference: 'All types',
  specialRequirements: '',
  website: '',
};

const INTERESTS = ['Temples', 'Adventure', 'Beaches', 'Food', 'Nature',
  'Photography', 'Shopping', 'Nightlife', 'Wellness', 'Local experiences'];

const STEPS = [
  { id: 0, label: 'Where and when' },
  { id: 1, label: 'How you travel' },
  { id: 2, label: 'About you' },
];

const partySizeSuggestion = (count) => {
  if (count <= 1) return 'Solo';
  if (count === 2) return 'Couple';
  if (count <= 4) return 'Family';
  return 'Group';
};

// One traveller can only be Solo; more than one can be anything except Solo
const travelWithOptionsFor = (count) =>
  (count <= 1 ? ['Solo'] : ['Couple', 'Family', 'Friends', 'Group']);

// Each step blocks progress on its own required fields, so errors surface early
const REQUIRED = [
  { destination: 'Choose a destination', travelers: 'How many of you?' },
  { budget: 'Give us a rough budget', accommodation: 'Pick a comfort level' },
  { name: 'We need a name', email: 'We need an email to reply to' },
];

const PlanTrip = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  // once the traveller picks a value we stop overwriting it, but impossible combinations still get corrected
  const travelWithTouched = useRef(false);

  useEffect(() => {
    fetch(`${API_URL}/api/csrf-token`)
      .then((r) => r.json())
      .then((d) => setCsrfToken(d.csrfToken))
      .catch(() => {});
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'travelWith') travelWithTouched.current = true;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'travelers') {
        const count = parseInt(value, 10);
        if (Number.isFinite(count)) {
          const stillValid = travelWithOptionsFor(count).includes(prev.travelWith);
          if (!stillValid || !travelWithTouched.current) {
            next.travelWith = partySizeSuggestion(count);
          }
        }
      }

      return next;
    });
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const toggleInterest = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const validateStep = (index) => {
    const found = {};
    Object.entries(REQUIRED[index]).forEach(([field, message]) => {
      if (!String(formData[field]).trim()) found[field] = message;
    });
    if (index === 2 && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      found.email = 'That email address does not look right';
    }
    if (index === 0 && formData.startDate && formData.endDate &&
        formData.endDate < formData.startDate) {
      found.endDate = 'The return date is before the departure date';
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) return;

    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      let token = csrfToken;
      if (!token) {
        const tokenResponse = await fetch(`${API_URL}/api/csrf-token`);
        token = (await tokenResponse.json()).csrfToken;
      }

      // Blank optional fields are omitted rather than sent as '', which the API rejects
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([key, value]) => value !== '' || key === 'website')
      );

      const response = await fetch(`${API_URL}/api/plan-trip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ ...payload, csrfToken: token }),
      });
      const data = await response.json();

      if (data.success) {
        track('generate_lead', {
          destination: payload.destination || 'unspecified',
          travellers: payload.travelers || 'unspecified',
        });
        setStatus({ type: 'success', message: data.requestId || 'N/A' });
        setFormData(EMPTY_FORM);
        const fresh = await fetch(`${API_URL}/api/csrf-token`);
        setCsrfToken((await fresh.json()).csrfToken);
      } else {
        setStatus({ type: 'error', message: data.message || data.error || 'Something went wrong. Please try again.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Unable to submit your request. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const field = (name) => (errors[name] ? 'field has-error' : 'field');

  if (status.type === 'success') {
    return (
      <div className="plan-page">
        <Helmet><title>Request received | Safarnamah</title></Helmet>
        <div className="container plan-done">
          <FaCheckCircle aria-hidden="true" />
          <h1>That&rsquo;s with us.</h1>
          <p>
            Your request reference is <strong>{status.message}</strong>. We&rsquo;ll come
            back within 24 hours with an itinerary and a realistic cost estimate.
          </p>
          <p className="plan-done-note">
            Anything to add? Reply to us at <a href="mailto:contact@safarnamah.in">contact@safarnamah.in</a>.
          </p>
          <div className="plan-done-actions">
            <Link to="/places" className="btn btn-primary">Browse the atlas</Link>
            <Link to="/blog" className="btn btn-secondary">Read a guide</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="plan-page">
      <Helmet>
        <title>Plan Your Trip - Safarnamah by Saket &amp; Pallavi | Custom Travel Planning</title>
        <meta name="description" content="Let Saket Bundele and Pallavi Korade help you plan your perfect Asian adventure. Custom itineraries for Thailand, Vietnam, Bali, Sri Lanka, and more." />
        <meta name="keywords" content="plan trip asia, custom travel planning, saket bundele travel, pallavi korade travel, asia itinerary" />
      </Helmet>
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Plan your trip', url: 'https://safarnamah.in/plan-trip' },
      ]} />

      <section className="plan-hero">
        <div className="container">
          <span className="eyebrow">Plan your trip</span>
          <h1>Tell us where you want to go.</h1>
          <p>
            Three short steps. We&rsquo;ll come back within 24 hours with an itinerary
            built from trips we&rsquo;ve actually taken &mdash; free, and with real costs.
          </p>
        </div>
      </section>

      <div className="plan-steps">
        <div className="container plan-steps-in">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={`plan-step ${step === s.id ? 'on' : ''} ${step > s.id ? 'done' : ''}`}
            >
              <span className="plan-step-num">{step > s.id ? <FaCheckCircle aria-hidden="true" /> : s.id + 1}</span>
              <span className="plan-step-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <section className="plan-body">
        <div className="container">
          <form className="plan-form" onSubmit={handleSubmit} noValidate>
            {/* honeypot: real people never fill this in */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
              className="hp"
            />

            {step === 0 && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <div className={field('destination')}>
                  <label htmlFor="destination">Where are you going?</label>
                  <select id="destination" name="destination" value={formData.destination} onChange={handleChange}>
                    <option value="">Choose a destination</option>
                    {destinations.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                    <option value="Somewhere else">Somewhere else &mdash; tell us below</option>
                  </select>
                  {errors.destination && <span className="err">{errors.destination}</span>}
                </div>

                <div className="row">
                  <div className="field">
                    <label htmlFor="startDate">Leaving</label>
                    <input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleChange} />
                  </div>
                  <div className={field('endDate')}>
                    <label htmlFor="endDate">Coming back</label>
                    <input type="date" id="endDate" name="endDate" value={formData.endDate} onChange={handleChange} min={formData.startDate} />
                    {errors.endDate && <span className="err">{errors.endDate}</span>}
                  </div>
                </div>

                <div className="row">
                  <div className={field('travelers')}>
                    <label htmlFor="travelers">How many of you?</label>
                    <input type="number" id="travelers" name="travelers" value={formData.travelers} onChange={handleChange} min="1" max="20" />
                    {errors.travelers && <span className="err">{errors.travelers}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="travelWith">Travelling as</label>
                    <select id="travelWith" name="travelWith" value={formData.travelWith} onChange={handleChange}>
                      {travelWithOptionsFor(parseInt(formData.travelers, 10) || 1)
                        .map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="duration">Roughly how long? <span className="opt">optional</span></label>
                  <input type="text" id="duration" name="duration" value={formData.duration} onChange={handleChange} placeholder="e.g. 10 days, or 2 weeks if we can stretch it" />
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <div className={field('budget')}>
                  <label htmlFor="budget">Budget per person, all in (USD)</label>
                  <select id="budget" name="budget" value={formData.budget} onChange={handleChange}>
                    <option value="">Choose a range</option>
                    {['Under $500', '$500 – $1,000', '$1,000 – $2,000', '$2,000 – $3,500', '$3,500+', 'Flexible']
                      .map((o) => <option key={o}>{o}</option>)}
                  </select>
                  {errors.budget && <span className="err">{errors.budget}</span>}
                </div>

                <div className="field">
                  <label>What are you into? <span className="opt">pick any</span></label>
                  <div className="chips chips-form">
                    {INTERESTS.map((i) => (
                      <button
                        key={i}
                        type="button"
                        className={`chip ${formData.interests.includes(i) ? 'on' : ''}`}
                        aria-pressed={formData.interests.includes(i)}
                        onClick={() => toggleInterest(i)}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="row">
                  <div className={field('accommodation')}>
                    <label htmlFor="accommodation">Where you&rsquo;d like to stay</label>
                    <select id="accommodation" name="accommodation" value={formData.accommodation} onChange={handleChange}>
                      {['Budget', 'Standard', 'Comfort', 'Luxury', 'A mix'].map((o) => <option key={o}>{o}</option>)}
                    </select>
                    {errors.accommodation && <span className="err">{errors.accommodation}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="transportation">Getting around</label>
                    <select id="transportation" name="transportation" value={formData.transportation} onChange={handleChange}>
                      {['Flexible', 'Public transport', 'Private driver', 'Self-drive / rental'].map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="mealPreference">Anything we should know about food?</label>
                  <select id="mealPreference" name="mealPreference" value={formData.mealPreference} onChange={handleChange}>
                    {['All types', 'Vegetarian', 'Vegan', 'Halal', 'Jain', 'Gluten-free'].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <div className="row">
                  <div className={field('name')}>
                    <label htmlFor="name">Your name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} autoComplete="name" />
                    {errors.name && <span className="err">{errors.name}</span>}
                  </div>
                  <div className={field('email')}>
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} autoComplete="email" />
                    {errors.email && <span className="err">{errors.email}</span>}
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="phone">Phone <span className="opt">optional</span></label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} autoComplete="tel" />
                </div>

                <div className="field">
                  <label htmlFor="specialRequirements">Anything else? <span className="opt">optional</span></label>
                  <textarea
                    id="specialRequirements"
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Places you already know you want to see, accessibility needs, things you'd rather avoid — anything that helps."
                  />
                </div>

                {status.type === 'error' && (
                  <div className="form-status error">
                    <FaInfoCircle aria-hidden="true" />
                    <p>{status.message}</p>
                  </div>
                )}
              </motion.div>
            )}

            <div className="plan-actions">
              {step > 0 && (
                <button type="button" className="btn btn-secondary" onClick={back}>
                  <FaArrowLeft aria-hidden="true" /> Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button type="button" className="btn btn-primary" onClick={next}>
                  Continue <FaArrowRight aria-hidden="true" />
                </button>
              ) : (
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Sending…' : <><FaPaperPlane aria-hidden="true" /> Send my request</>}
                </button>
              )}
            </div>

            <p className="plan-note">
              We reply within 24 hours. No charge, and we won&rsquo;t pass your details on.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default PlanTrip;
